import {Context, Customer, Location} from "./Types";
import moment from "moment-timezone";
import {AutoCompleteOption} from "../Components/Autcomplete/Interfaces";
import {MappingProviders, PackagePickupStatuses} from "./Enums";
import {Duration} from "luxon";

export function isNullOrWhitespace(value: string): boolean {
    if (value == null)
        return true;

    if (value == "")
        return true;

    return value.replace(/\s/g, '').length === 0;
}

export function hasFullAddress(location: Location) {
    return (location != undefined
        && location.addressNumber
        && location.addressStreet
        && location.city
        && location.state
        && location.zip);
}

export function humanizeEnumName(enumName: string) {
    return enumName.replace(/([A-Z])/g, ' $1').trim()
}

export function getLocalDateTime(date: Date | string): Date {
    let d = moment(date).parseZone();
    return new Date(d.year(), d.month(), d.date(), d.hour(), d.minute());
}

export function getProximityParameter(latitude: number, longitude: number) {
    return `&proximity=${longitude},${latitude}`;
}

export function getProximityParameterByLocation(location: Location) {
    let proximityParam = "";
    if (location.latitude && location.longitude) {
        proximityParam = `&proximity=${location.longitude},${location.latitude}`;
    }
    return proximityParam;
}

export function metersToMiles(m: number) {
    return m * 0.00062137;
}

export function secondsToMinutes(seconds: number) : number {
    return Duration.fromMillis(seconds * 1000).shiftTo("minutes").minutes;
}

export function toSimpleLocation(location: Location) {
    return {
        addressNumber: location?.addressNumber,
        addressStreet: location?.addressStreet,
        address2: location?.address2,
        city: location?.city,
        dateCreated: location?.dateCreated,
        id: location?.id,
        name: location?.name,
        state: location?.state,
        zip: location?.zip,
        latitude: location?.latitude,
        longitude: location?.longitude,
        mapServiceId: location?.mapServiceId
    }
}

export function getPickupStatusNameByEnum(enumId: number) {
    switch (enumId) {
        case PackagePickupStatuses.PickedUp:
            return "Picked Up";
        case PackagePickupStatuses.Damaged:
            return "Damaged";
        case PackagePickupStatuses.Cancelled:
            return "Cancelled";
        case PackagePickupStatuses.Other:
            return "Other";
        case PackagePickupStatuses.Unknown:
            return "Unknown";
        case PackagePickupStatuses.NotAvailable:
            return "Not Available";
    }
}

export function getLocationFromAutoCompleteOption(selection: AutoCompleteOption) : Location {
    // if (selection.returnValue.address == undefined) {
    //     return null;
    // }

    let address: Location = {
        address2: '',
        addressNumber: '',
        addressStreet: '',
        city: '',
        dateCreated: '',
        id: '',
        latitude: 0,
        longitude: 0,
        mapServiceId: '',
        name: '',
        state: '',
        zip: ''
    };

    let subcity = selection.returnValue.context.find((c: Context) => {
        return c.id.startsWith('locality');
    });

    if (subcity) {
        address.city = subcity.text;
    } else {
        address.city = selection.returnValue.context.find((c: Context) => {
            return c.id.startsWith('place');
        }).text;
    }

    address.state = selection.returnValue.context.find((c: Context) => {
        return c.id.startsWith('region');
    }).short_code.split("US-")[1];

    address.zip = selection.returnValue.context.find((c: Context) => {
        return c.id.startsWith('postcode');
    })?.text;

    address.longitude = selection.returnValue.geometry.coordinates[0];
    address.latitude = selection.returnValue.geometry.coordinates[1];

    address.addressNumber = selection.returnValue.address;
    address.addressStreet = selection.returnValue.text;
    address.mapServiceId = selection.returnValue.id;
    address.formattedAddress = getFormattedAddress(address);

    //TODO.MV: Set mappingProviderEnumId based on selection
    address.mappingProviderEnumId = MappingProviders.Mapbox;
    address.geocodeObtainedAtUtc = moment.utc(new Date).toISOString();

    // ironically, the context table does not seem to include the zip code if the place type is itself a zip code
    if (selection.returnValue.place_type.find((v: string) => v == 'postcode')) {
        address.addressNumber = address.addressStreet = null;
        address.zip = selection.returnValue.text;
    }

    return address;
}

export function getProximityParameterForCustomer(customer: Customer) {
    let defaultLocation = customer?.addressBookEntries?.find(a => a.isDefault);

    if (defaultLocation)
        return getProximityParameter(defaultLocation.location.latitude, defaultLocation.location.longitude);
}

export function getFormattedAddress(address: Location) {
    return `${address.addressNumber ? `${address.addressNumber } ` : ""}${address.addressStreet ? `${address.addressStreet},` : ""}${address.address2 ? ` ${address.address2},` : ''} ${address.city}, ${address.state} ${address.zip}`;
}

export function getFullTimezoneOffsetMs(timeZone: string): number {
    // user local time zone may not necessarily reflect that of the time zone being affected
    // this logic sets the cumulative offset by which the local time should be changed to reflect the customer's time.
    const localDateTime = new Date();
    const localBaseline = localDateTime.setHours(0,0,0,0);
    localDateTime.setUTCHours(0,0,0,0);

    const localOffsetMs = localDateTime.getTimezoneOffset() * -1 * 60 * 1000;

    const customerDateTime = moment(localDateTime.toISOString()).tz(timeZone).local(true).toDate();
    const customerOffsetMs = (customerDateTime.getTime() - localBaseline) * -1;

    return localOffsetMs + customerOffsetMs;
}

export function getDelocalizedDateISO(date: Date, timeZone: string): string | null {
    return date ? new Date(date.getTime() + getFullTimezoneOffsetMs(timeZone)).toISOString() : null;
}

export function validateAddress(location: Location): { isError: boolean, messages: string[] } {
    let isError = false;
    const messages = new Array<string>();

    if (isNullOrWhitespace(location.addressStreet) && (!location.mapServiceId || !location.mapServiceId.startsWith("postcode"))) {
        isError = true;
        messages.push("Please enter a valid Address Line 1");
    }

    if (isNullOrWhitespace(location.city)) {
        isError = true;
        messages.push("Please enter a City");
    }

    if (isNullOrWhitespace(location.state)) {
        isError = true;
        messages.push("Please enter a State");
    }

    if (isNullOrWhitespace(location.zip)) {
        isError = true;
        messages.push("Please enter a Zip");
    }

    return { isError, messages };
}